<template>
  <el-dialog @close="onDialogClosing" :width="'100%'" :fullscreen="true" :destroy-on-close="true" :lock-scroll="true"
    :close-on-click-modal="false" v-model="syncedModel" custom-class="dialog-ocr">
    <template #title>
      <div class="flex justify-center items-center">
        <div class="label-round">
          <span v-if="scanMode === 'add'">{{ $t('scan_to_add_items') }}</span>
          <span v-else>{{ $t('scan_to_remove_items') }}</span>
        </div>
      </div>
    </template>
    <div v-loading.fullscreen="loading" v-show="!loading" class="scan-view">
      <div class="barcode-scanner">
        <div id="scan-area">
          <div class="scan-selection-camera" @click="openDialog" v-show="cameras.length > 0">
            <IconSwitch />
          </div>
          <div id="video-area">
            <video playsinline autoplay id="video"></video>
          </div>
        </div>

        <BarcodeSymbologiesSelection class="absolute bottom-2 z-50 left-8 flex w-52"
          @update:onSymbologiesChange="setBarcodeSymbologies" v-model:symbologies="symbologies" />
      </div>

      <!-- Display current count of scanned items -->
      <div class="existing-items-counter"
        :class="{ 'remove-mode': scanMode === 'remove', 'add-mode': scanMode === 'add' }" @click="showItemsList">
        <div class="counter-message">{{ $t('number_of_scanned_items') }}</div>
        <!-- <div class="counter-badge">
          <span class="counter-value">{{ scannedValues.length }}</span>
        </div> -->

        <span class="label-box">
          {{ scannedValues.length }}
        </span>
      </div>

      <!-- Error message display -->
      <div v-if="validationError && scanMode === 'add'" class="error-msg-container">
        <span class="error-msg">{{ validationError }}</span>
      </div>

      <div class="step-button">
        <el-button class="el-button--primary is-circle is-medium" @click="onDialogClosing">
          {{ $t('ok') }}
          <KeyenceHTHardKeyButton
            v-if="isKeyenceEnterpriseBrowser"
            :hardkey="KeyenceHardKey.ENTER"
            :function="onDialogClosing"
            :visible="syncedModel"
            :newLayer="true"
          />
        </el-button>
      </div>
    </div>
    <SelectionCameraPopup v-model:selectedCamera="selectedCamera" :cameras="cameras" v-model:isShow="isOpenDialog" />

    <!-- Items List Popup -->
    <el-dialog v-model="showItemsListDialog" :lock-scroll="true" center width="95%" top="0" :show-close="false"
      :destroy-on-close="false" :title="$t('list_scanned_items')" custom-class="items-list-dialog"
      :append-to-body="true">
      <div class="items-list-container">
        <div v-if="scannedValues.length === 0" class="empty-state">
          <p>{{ $t('no_items_scanned_yet') }}</p>
        </div>
        <div v-else class="table-container">
          <table class="items-table">
            <thead>
              <tr class="table-header">
                <th class="trolley-cell">
                  {{ $t(trolleyHeaderTitle) }}
                </th>
                <th class="code-cell">{{ $t('serial') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(item, index) in scannedValues" :key="index" class="table-row">
                <td class="trolley-cell">
                  <span v-if="item.trollyNumber" class="trolley-badge trolley-number-badge">
                    <IconTrolley class="trolley-icon-small" />
                    {{ item.trollyNumber }}
                  </span>
                  <span v-if="item.trollyIndex !== undefined && item.trollyIndex !== null"
                    class="trolley-badge trolley-index-badge">
                    #{{ item.trollyIndex }}
                  </span>
                </td>
                <td class="code-cell">
                  {{ item.code }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
      <template #footer>
        <el-button class="el-button--primary is-circle is-medium" @click="showItemsListDialog = false">
          {{ $t('ok') }}
          <KeyenceHTHardKeyButton
            v-if="isKeyenceEnterpriseBrowser"
            :hardkey="KeyenceHardKey.ENTER"
            :function="() => (showItemsListDialog = false)"
            :visible="showItemsListDialog"
            :newLayer="true"
          />
        </el-button>
      </template>
    </el-dialog>
  </el-dialog>
</template>

<script lang="ts">
import BarcodeSymbologiesSelection from '@/components/BarcodeSymbologiesSelection.vue'
import SelectionCameraPopup from '@/components/dialog/SelectionCameraPopup.vue'
import KeyenceHTHardKeyButton from '@/components/keyenceHT/hardkeys/KeyenceHTHardKeyButton.vue'
import BarcodeReaderMixin from '@/components/mixins/BarcodeReaderMixin.vue'
import IconSwitch from '@/components/svg/IconSwitch.vue'
import IconTrolley from '@/components/svg/IconTrolley.vue'
import { BARCODE_SCAN } from '@/utils/constants'
import { EBCRType, EOperatorType } from '@/utils/enums'
import errorHandler from '@/utils/errorHandler'
import { formatDateTimeListString, promptVideoPermission } from '@/utils/helpers'
import { IListStringItem } from '@/utils/types/commonTypes'
import { KeyenceHardKey } from '@/utils/keyenceHT/keyenceHardKeyEventHandler'
import { isKeyenceEnterpriseBrowser } from '@/utils/keyenceHT/keyenceModules'
import { IProject } from '@/utils/types/projectTypes'
import { BrowserMultiFormatReader, Result } from '@zxing/library'
import 'cropperjs/dist/cropper.css'
import { Barcode, BarcodeCapture } from 'scandit-web-datacapture-barcode'
import { mixins, Options } from 'vue-class-component'
import { Prop } from 'vue-property-decorator'

// Mixins
@Options({
  components: {
    BarcodeSymbologiesSelection,
    IconSwitch,
    IconTrolley,
    SelectionCameraPopup,
    KeyenceHTHardKeyButton,
  },
  emits: ['update:modelValue', 'update:confirm', 'confirm:listSerialCodes'],
  inheritAttrs: false,
  name: 'ScanComponent',
  methods: { formatDateTimeListString },
})
export default class ListStringScanComponent extends mixins(BarcodeReaderMixin) {
  KeyenceHardKey = KeyenceHardKey

  get isKeyenceEnterpriseBrowser() {
    return isKeyenceEnterpriseBrowser()
  }

  @Prop({ type: Boolean, default: false }) readonly modelValue!: boolean
  @Prop({ type: Array, default: [] }) readonly scannedValues!: IListStringItem[]
  @Prop({ default: EBCRType.DEFAULT }) customBarcodECameraReaderType!: EBCRType // This will override customBarcodECameraReaderType from mixins
  @Prop({ type: [String, Number] }) readonly initialValue?: string | number
  @Prop({ type: Number }) readonly minValue?: number
  @Prop({ type: Number }) readonly maxValue?: number
  @Prop({ type: String, default: 'add' }) readonly scanMode!: 'add' | 'remove'
  @Prop({ type: String }) readonly operatorType?: string
  codeReader: BrowserMultiFormatReader | null = new BrowserMultiFormatReader(
    undefined,
    BARCODE_SCAN.timeBetweenScansMillis as number
  )

  _barcodeDataCapture: BarcodeCapture | undefined
  loading = true
  video: HTMLVideoElement | null = null
  pictureWidth = 320
  pictureHeight = 320
  isCaptured = false
  showItemsListDialog = false

  confirm() {
    this.$emit('update:confirm', this.valueCode.trim())
  }

  showItemsList() {
    if (this.scannedValues.length > 0) {
      this.showItemsListDialog = true
    }
  }

  onDialogClosing() {
    this.$emit('update:modelValue', false)
    this.$emit('confirm:listSerialCodes')
  }

  get syncedModel() {
    return this.modelValue
  }

  set syncedModel(value: boolean) {
    this.$emit('update:modelValue', value)
  }

  defaultScanReader(result: Result, codeReader: BrowserMultiFormatReader) {
    this.codeReader = codeReader
    const tempResult: { [key: string]: unknown } = { ...result }
    if (!tempResult?.text) {
      return
    }
    this.handleScanResult((tempResult.text as string) || '')
  }

  scanditDataResult(symbology: Barcode) {
    if (!symbology) return
    this.handleScanResult((symbology?.data as string) || '')
  }

  async barcodeDetectorPolyFillScanReader(result: DetectedBarcode[]) {
    if (result.length < 1) return
    const firstDetectedBarcode = result[0]?.rawValue || ''
    this.handleScanResult(firstDetectedBarcode)
  }

  handleScanResult(plainText: string) {
    this.valueCode = plainText
    // Automatically call confirm when a new value is scanned
    if (plainText.trim()) {
      this.confirm()
    }
  }

  get barcode() {
    return this.$store.state.barcode.barcode
  }

  get project(): IProject {
    return this.$store.state.project.details
  }

  get validationError(): string {
    const length = this.scannedValues.length

    if (this.minValue !== undefined && length < this.minValue) {
      return this.$t('minimum_items_required', { minLength: this.minValue })
    }

    if (this.maxValue !== undefined && length > this.maxValue) {
      return this.$t('maximum_items_required', { maxLength: this.maxValue })
    }

    return ''
  }

  get hasValidationError(): boolean {
    return !!this.validationError
  }

  get trolleyHeaderTitle(): string {
    return this.operatorType === EOperatorType.CORRECT_VALUE ? 'trolly_info_correct' : 'trolly_info'
  }

  async mounted() {
    // Disable multiple scan for immediate confirmation
    this.isShowMultipleScan = false
    this.isMultipleScan = false

    // Set initial value if provided
    if (this.initialValue) {
      this.valueCode = this.initialValue.toString()
    }

    await this.initCameraSelection()
    if (this.isCustomVideoReader) {
      await promptVideoPermission()
    }

    await this.setupCameraSetting()
  }

  get isCustomVideoReader() {
    return !(this.customBarcodECameraReaderType === EBCRType.SCANDIT)
  }

  async setupCameraSetting() {
    switch (this.barcodeReaderType as EBCRType) {
      case EBCRType.SCANDIT:
        await this.initScanditReader()
        break
      case EBCRType.BARCODE_DETECTION:
        await this.initBarcodeReaderPolyFill()
        break
      default:
        await promptVideoPermission()
        await this.initZxingReader()
        break
    }
  }

  get isScanditReader() {
    return this.customBarcodECameraReaderType === EBCRType.SCANDIT
  }

  async setupVideo() {
    if (!this.video) {
      this.video = document.getElementById('video') as HTMLVideoElement
    } else {
      this.video.srcObject = null
    }
    if (this.selectedCamera) {
      await navigator.mediaDevices
        .getUserMedia({
          video: {
            width: { ideal: 1000 },
            height: { ideal: 1000 },
            frameRate: {
              ideal: 30,
              max: 60,
            },
            aspectRatio: 1,
            deviceId: { exact: this.selectedCamera },
          },
        })
        .then(async (stream) => {
          await this.setStream(stream)
        })
        .catch((e) => {
          console.log('error', e)
          errorHandler(e.name === 'NotAllowedError' ? 'camera_access_not_allow' : e)
        })
        .finally(() => {
          this.loading = false
        })
    } else {
      await navigator.mediaDevices
        .getUserMedia({
          video: {
            facingMode: 'environment',
            aspectRatio: 1,
          },
        })
        .then(async (stream) => {
          await this.setStream(stream)
        })
        .catch((e) => {
          errorHandler(e.name === 'NotAllowedError' ? 'camera_access_not_allow' : e)
        })
        .finally(() => {
          this.loading = false
        })
    }
  }

  async handleMultipleScan(plainText: string) {
    if (this.isHandleMultipleScan) {
      return
    }

    // For single scan mode, just confirm immediately
    this.valueCode = plainText
    this.confirm()
  }
}
</script>
<style lang="scss" scoped>
@import './src/assets/css/mixins.scss';

.margin-ocr {
  margin-top: 10px;
}

.margin-top-reader {
  margin-top: 16px;
}

.step-button {
  display: flex;
  justify-content: center;
}

.media-container {
  width: 100%;
  position: relative;
  height: 40vh;

  video {
    top: 0;
    left: 0;
    width: 100%;
    object-fit: cover;
    height: 40vh;
  }
}

.media-container:deep() {
  &.scandit-container {
    top: 0;
    right: 0;
    width: 100%;
    height: 100%;
  }
}

.dectect-input {
  padding: 0 30px 0 30px;
  text-align-last: center;
}

.scan-view {
  text-align: center;
}

.message-wrapper {
  margin-top: 10px;
  word-break: break-word;
  position: absolute;
  z-index: 100;
  color: $pure-white;
  text-align: center;
  padding: 0 2rem;
  width: 100%;
  box-sizing: border-box;
  // font-size: 0.85rem;

  &.top {
    margin-top: 10px;

    >div {
      padding: 8px 12px;
      background: rgb(0, 0, 0, 0.7);
      border-radius: 5px;
    }
  }
}

.image-container {
  max-width: 100%;
  height: 40vh;
  display: none;
  object-fit: cover;
}

.img {
  max-width: 100%;
  height: 40vh;
}

.barcode-scanner {
  height: 60vh;
  background: rgb(32, 32, 32);
  position: relative;
}

.barcode-scanner video {
  width: 100%;
  height: 100%;
}

.existing-items-counter {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  margin: 16px 20px;
  background-color: #f8f9fa;
  border-radius: 12px;
  border: 1px solid #e9ecef;
  transition: all 0.3s ease;
  cursor: pointer;

  &.add-mode {
    background: linear-gradient(135deg, #e3f2fd 0%, #f0f7ff 100%);
    border-color: #409eff;

    .counter-badge {
      background-color: #409eff;
    }
  }

  &.remove-mode {
    background: linear-gradient(135deg, #fef2f2 0%, #fef7f7 100%);
    border-color: #f56c6c;

    .counter-badge {
      background-color: #f56c6c;
    }
  }

  .counter-message {
    font-weight: 500;
    color: #333;
    flex: 1;
    margin-right: 12px;
    line-height: 1.4;
  }

  .counter-badge {
    display: flex;
    align-items: center;
    justify-content: center;
    min-width: 40px;
    height: 32px;
    border-radius: 16px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    transition: transform 0.2s ease;

    .counter-value {
      color: white;
      font-weight: 600;
      // font-size: 14px;
      text-align: center;
    }
  }
}

// Items List Popup Styles
:deep(.items-list-dialog) {
  .el-dialog__header {
    background-color: #f8f9fa;
    border-bottom: 1px solid #e9ecef;
    border-radius: 8px 8px 0 0;
  }

  .el-dialog__title {
    font-weight: 600;
    color: #333;
  }
}

.error-msg-container {
  text-align: center;
  padding: 16px 20px 8px 20px;
}

.error-msg {
  color: #f56c6c;
  // font-size: 14px;
  font-weight: 500;
  line-height: 1.2;
  background-color: rgba(245, 108, 108, 0.1);
  padding: 8px 12px;
  border-radius: 6px;
  border: 1px solid rgba(245, 108, 108, 0.3);
}

.items-list-container {
  max-height: 60vh;
  overflow-y: auto;

  .empty-state {
    text-align: center;
    padding: 40px 20px;
    color: #666;
    font-style: italic;
  }

  .table-container {
    max-width: 100%;
    overflow-y: auto;
    overflow-x: hidden;
    border: 2px solid #e0e0e0;
    border-radius: 8px;
    padding: 0;
  }

  .items-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 22px;

    .table-header {
      background-color: #f5f7fa;
      border-bottom: 2px solid #e0e0e0;

      th {
        padding: 10px 6px;
        text-align: center;
        font-weight: 600;
        color: #555;
        font-size: 20px;
        text-transform: uppercase;
        letter-spacing: 0.5px;
        border-right: 1px solid #e9ecef;

        &:last-child {
          border-right: none;
        }

        &.code-cell {
          text-align: left;
        }
      }
    }

    .table-row {
      border-bottom: 1px solid #f0f0f0;

      &:hover {
        background-color: #f9f9f9;
      }
    }

    td,
    th {
      padding: 8px 6px;
      vertical-align: middle;
      border-right: 1px solid #f0f0f0;

      &:last-child {
        border-right: none;
      }
    }

    .index-cell {
      width: 40px;
      text-align: center;
      font-weight: 600;
      color: #666;
      // font-size: 11px;
    }

    .trolley-cell {
      width: 120px;
      text-align: center;
      // font-size: 11px;
      display: flex;
      justify-content: center;
      align-items: center;
      gap: 4px;

      .trolley-badge {
        display: inline-flex;
        align-items: center;
        gap: 3px;
        padding: 3px 6px;
        border-radius: 6px;
        font-weight: 600;
        white-space: nowrap;
        // font-size: 10px;
      }

      .trolley-number-badge {
        background-color: #f3f4f6;
        color: #333;
        border: 1px solid #333;

        .trolley-icon-small {
          width: 10px;
          height: 10px;
          flex-shrink: 0;
        }
      }

      .trolley-index-badge {
        background-color: #e9ecef;
        color: #666;
        border: 1px solid #adb5bd;
      }

      .empty-value {
        color: #ccc;
        font-style: italic;
      }
    }

    .code-cell {
      width: auto;
      font-weight: 500;
      color: #333;
      text-align: left;
    }
  }
}
</style>
